import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  FaStar, FaShieldAlt, FaClock, FaCreditCard, FaMobileAlt, 
  FaHeadset, FaGift, FaCheckCircle, FaTimesCircle, FaArrowLeft,
  FaChartBar, FaTrophy, FaUsers, FaGamepad, FaRocket
} from 'react-icons/fa';
import { 
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

const CasinoReview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'overview' | 'games' | 'bonuses' | 'reviews'>('overview');

  const casinoData: Record<string, any> = {
    allbritishcasino: {
      name: 'All British Casino',
      image: '/images/offers/allbritishcasino.webp',
      url: 'https://www.allbritishcasino.com/',
      rating: 4.8,
      totalReviews: 2847,
      established: '2013',
      license: 'UK Gambling Commission',
      minDeposit: '£10',
      withdrawalTime: '24-48 hours',
      currencies: ['GBP', 'EUR', 'USD'],
      languages: ['English'],
      support: '24/7 Live Chat, Email',
      bonus: '100% up to £100 + 50 Free Spins',
      wagering: '35x',
      maxBet: '£5',
      validFor: '30 days',
      description: 'All British Casino brings the quintessential British gaming experience to your screen. With a focus on classic elegance and modern innovation, this casino has become a favorite among UK players seeking reliability and quality.',
      pros: [
        'Fast withdrawal processing',
        'Excellent customer support',
        'Wide selection of UK-friendly payment methods',
        'Strong security measures',
        'Regular promotions for existing players'
      ],
      cons: [
        'Limited cryptocurrency options',
        'Some games restricted in certain regions'
      ],
      games: {
        slots: 1200,
        tableGames: 85,
        liveDealer: 45,
        jackpots: 12
      },
      radarData: [
        { subject: 'Game Variety', A: 95, fullMark: 100 },
        { subject: 'Bonuses', A: 88, fullMark: 100 },
        { subject: 'Support', A: 92, fullMark: 100 },
        { subject: 'Security', A: 98, fullMark: 100 },
        { subject: 'Payout Speed', A: 90, fullMark: 100 },
        { subject: 'Mobile', A: 85, fullMark: 100 },
      ],
      monthlyData: [
        { month: 'Jan', withdrawals: 450, deposits: 1200, players: 3200 },
        { month: 'Feb', withdrawals: 520, deposits: 1400, players: 3500 },
        { month: 'Mar', withdrawals: 610, deposits: 1600, players: 3800 },
        { month: 'Apr', withdrawals: 680, deposits: 1800, players: 4100 },
        { month: 'May', withdrawals: 750, deposits: 2000, players: 4400 },
        { month: 'Jun', withdrawals: 820, deposits: 2200, players: 4700 },
      ],
      gameDistribution: [
        { name: 'Slots', value: 65, color: '#00f0ff' },
        { name: 'Table Games', value: 20, color: '#b026ff' },
        { name: 'Live Casino', value: 10, color: '#ff00f5' },
        { name: 'Jackpots', value: 5, color: '#00ff88' },
      ]
    },
    funcasino: {
      name: 'Fun Casino',
      image: '/images/offers/funcasino.webp',
      url: 'https://www.funcasino.com/',
      rating: 4.7,
      totalReviews: 2156,
      established: '2015',
      license: 'UK Gambling Commission',
      minDeposit: '£20',
      withdrawalTime: '48-72 hours',
      currencies: ['GBP', 'EUR'],
      languages: ['English'],
      support: '24/7 Live Chat, Email, Phone',
      bonus: '200% up to £200',
      wagering: '40x',
      maxBet: '£5',
      validFor: '30 days',
      description: 'Fun Casino lives up to its name by delivering an entertaining and vibrant gaming environment. With a focus on fun and excitement, this casino combines a massive game library with generous bonuses and a user-friendly platform.',
      pros: [
        'Extensive game collection',
        'Generous welcome bonus',
        'VIP rewards program',
        'Mobile-optimized platform',
        'Multiple payment options'
      ],
      cons: [
        'Slightly longer withdrawal times',
        'Higher minimum deposit requirement'
      ],
      games: {
        slots: 1500,
        tableGames: 120,
        liveDealer: 60,
        jackpots: 20
      },
      radarData: [
        { subject: 'Game Variety', A: 98, fullMark: 100 },
        { subject: 'Bonuses', A: 92, fullMark: 100 },
        { subject: 'Support', A: 88, fullMark: 100 },
        { subject: 'Security', A: 95, fullMark: 100 },
        { subject: 'Payout Speed', A: 82, fullMark: 100 },
        { subject: 'Mobile', A: 90, fullMark: 100 },
      ],
      monthlyData: [
        { month: 'Jan', withdrawals: 380, deposits: 1100, players: 2800 },
        { month: 'Feb', withdrawals: 450, deposits: 1300, players: 3100 },
        { month: 'Mar', withdrawals: 520, deposits: 1500, players: 3400 },
        { month: 'Apr', withdrawals: 590, deposits: 1700, players: 3700 },
        { month: 'May', withdrawals: 660, deposits: 1900, players: 4000 },
        { month: 'Jun', withdrawals: 730, deposits: 2100, players: 4300 },
      ],
      gameDistribution: [
        { name: 'Slots', value: 70, color: '#00f0ff' },
        { name: 'Table Games', value: 15, color: '#b026ff' },
        { name: 'Live Casino', value: 10, color: '#ff00f5' },
        { name: 'Jackpots', value: 5, color: '#00ff88' },
      ]
    },
    playojo: {
      name: 'PlayOJO',
      image: '/images/offers/playojo.webp',
      url: 'https://www.playojo.com',
      rating: 4.9,
      totalReviews: 3421,
      established: '2017',
      license: 'UK Gambling Commission',
      minDeposit: '£10',
      withdrawalTime: '24 hours',
      currencies: ['GBP'],
      languages: ['English'],
      support: '24/7 Live Chat, Email',
      bonus: '50 Free Spins (No Wagering)',
      wagering: '0x',
      maxBet: 'N/A',
      validFor: '7 days',
      description: 'PlayOJO revolutionizes the online casino experience with its unique OJOplus rewards system and no-wagering bonuses. This innovative approach has made it one of the most player-friendly casinos in the UK market.',
      pros: [
        'No wagering requirements on bonuses',
        'OJOplus cashback on every bet',
        'Fastest withdrawal times',
        'Transparent terms and conditions',
        'Excellent mobile app'
      ],
      cons: [
        'Limited bonus offers compared to competitors',
        'Smaller game selection than some rivals'
      ],
      games: {
        slots: 800,
        tableGames: 60,
        liveDealer: 35,
        jackpots: 8
      },
      radarData: [
        { subject: 'Game Variety', A: 80, fullMark: 100 },
        { subject: 'Bonuses', A: 100, fullMark: 100 },
        { subject: 'Support', A: 94, fullMark: 100 },
        { subject: 'Security', A: 96, fullMark: 100 },
        { subject: 'Payout Speed', A: 98, fullMark: 100 },
        { subject: 'Mobile', A: 92, fullMark: 100 },
      ],
      monthlyData: [
        { month: 'Jan', withdrawals: 520, deposits: 1300, players: 4200 },
        { month: 'Feb', withdrawals: 600, deposits: 1500, players: 4600 },
        { month: 'Mar', withdrawals: 680, deposits: 1700, players: 5000 },
        { month: 'Apr', withdrawals: 760, deposits: 1900, players: 5400 },
        { month: 'May', withdrawals: 840, deposits: 2100, players: 5800 },
        { month: 'Jun', withdrawals: 920, deposits: 2300, players: 6200 },
      ],
      gameDistribution: [
        { name: 'Slots', value: 75, color: '#00f0ff' },
        { name: 'Table Games', value: 15, color: '#b026ff' },
        { name: 'Live Casino', value: 8, color: '#ff00f5' },
        { name: 'Jackpots', value: 2, color: '#00ff88' },
      ]
    },
    casinocasino: {
      name: 'CasinoCasino',
      image: '/images/offers/casinocasino.webp',
      url: 'https://www.casinocasino.com',
      rating: 4.6,
      totalReviews: 1789,
      established: '2014',
      license: 'UK Gambling Commission',
      minDeposit: '£10',
      withdrawalTime: '24-48 hours',
      currencies: ['GBP', 'EUR'],
      languages: ['English'],
      support: '24/7 Live Chat, Email',
      bonus: '£150 Welcome Bonus',
      wagering: '30x',
      maxBet: '£5',
      validFor: '30 days',
      description: 'CasinoCasino delivers a classic casino experience with a focus on traditional games and reliable service. This established operator combines timeless casino favorites with modern technology, creating a trusted platform for UK players who value consistency and quality.',
      pros: [
        'Classic game selection',
        'Reliable payout processing',
        'User-friendly interface',
        'Strong security measures',
        'Competitive welcome bonus'
      ],
      cons: [
        'Smaller game library than newer casinos',
        'Limited promotional activities'
      ],
      games: {
        slots: 1000,
        tableGames: 80,
        liveDealer: 45,
        jackpots: 12
      },
      radarData: [
        { subject: 'Game Variety', A: 88, fullMark: 100 },
        { subject: 'Bonuses', A: 86, fullMark: 100 },
        { subject: 'Support', A: 89, fullMark: 100 },
        { subject: 'Security', A: 95, fullMark: 100 },
        { subject: 'Payout Speed', A: 87, fullMark: 100 },
        { subject: 'Mobile', A: 86, fullMark: 100 },
      ],
      monthlyData: [
        { month: 'Jan', withdrawals: 400, deposits: 1100, players: 2900 },
        { month: 'Feb', withdrawals: 470, deposits: 1300, players: 3200 },
        { month: 'Mar', withdrawals: 550, deposits: 1500, players: 3500 },
        { month: 'Apr', withdrawals: 630, deposits: 1700, players: 3800 },
        { month: 'May', withdrawals: 710, deposits: 1900, players: 4100 },
        { month: 'Jun', withdrawals: 790, deposits: 2100, players: 4400 },
      ],
      gameDistribution: [
        { name: 'Slots', value: 70, color: '#00f0ff' },
        { name: 'Table Games', value: 18, color: '#b026ff' },
        { name: 'Live Casino', value: 10, color: '#ff00f5' },
        { name: 'Jackpots', value: 2, color: '#00ff88' },
      ]
    },
    mrplay: {
      name: 'MrPlay',
      image: '/images/offers/mrplay.webp',
      url: 'https://www.mrplay.com/',
      rating: 4.7,
      totalReviews: 2156,
      established: '2017',
      license: 'UK Gambling Commission',
      minDeposit: '£10',
      withdrawalTime: '24-48 hours',
      currencies: ['GBP', 'EUR', 'USD'],
      languages: ['English'],
      support: '24/7 Live Chat, Email',
      bonus: '100% + 200 Spins',
      wagering: '35x',
      maxBet: '£5',
      validFor: '30 days',
      description: 'MrPlay stands out with its massive game library and daily promotional activities. This casino combines quantity with quality, offering thousands of games alongside regular bonuses and promotions that keep players engaged and rewarded.',
      pros: [
        'Extensive game collection',
        'Daily promotions and bonuses',
        'Mobile-first design',
        'Regular tournaments',
        'Generous free spins offers'
      ],
      cons: [
        'Higher wagering requirements',
        'Can be overwhelming for new players'
      ],
      games: {
        slots: 1400,
        tableGames: 120,
        liveDealer: 65,
        jackpots: 25
      },
      radarData: [
        { subject: 'Game Variety', A: 98, fullMark: 100 },
        { subject: 'Bonuses', A: 91, fullMark: 100 },
        { subject: 'Support', A: 88, fullMark: 100 },
        { subject: 'Security', A: 94, fullMark: 100 },
        { subject: 'Payout Speed', A: 86, fullMark: 100 },
        { subject: 'Mobile', A: 92, fullMark: 100 },
      ],
      monthlyData: [
        { month: 'Jan', withdrawals: 460, deposits: 1250, players: 3400 },
        { month: 'Feb', withdrawals: 530, deposits: 1450, players: 3700 },
        { month: 'Mar', withdrawals: 610, deposits: 1650, players: 4000 },
        { month: 'Apr', withdrawals: 690, deposits: 1850, players: 4300 },
        { month: 'May', withdrawals: 770, deposits: 2050, players: 4600 },
        { month: 'Jun', withdrawals: 850, deposits: 2250, players: 4900 },
      ],
      gameDistribution: [
        { name: 'Slots', value: 71, color: '#00f0ff' },
        { name: 'Table Games', value: 16, color: '#b026ff' },
        { name: 'Live Casino', value: 10, color: '#ff00f5' },
        { name: 'Jackpots', value: 3, color: '#00ff88' },
      ]
    },
    partycasino: {
      name: 'PartyCasino',
      image: '/images/offers/partycasino.webp',
      url: 'https://www.partycasino.com/',
      rating: 4.6,
      totalReviews: 1923,
      established: '2011',
      license: 'UK Gambling Commission',
      minDeposit: '£10',
      withdrawalTime: '24-48 hours',
      currencies: ['GBP', 'EUR', 'USD'],
      languages: ['English'],
      support: '24/7 Live Chat, Email',
      bonus: '£100 Welcome Bonus',
      wagering: '30x',
      maxBet: '£5',
      validFor: '30 days',
      description: 'PartyCasino brings the party atmosphere to online gaming with vibrant themes, exciting tournaments, and a social gaming experience. Known for its Party Points rewards system and regular live tournaments, this casino creates a community feel that sets it apart.',
      pros: [
        'Party Points loyalty rewards',
        'Regular live tournaments',
        'Premium game selection',
        'Social gaming features',
        'Competitive wagering requirements'
      ],
      cons: [
        'Smaller bonus compared to some competitors',
        'Limited cryptocurrency options'
      ],
      games: {
        slots: 1100,
        tableGames: 95,
        liveDealer: 50,
        jackpots: 15
      },
      radarData: [
        { subject: 'Game Variety', A: 92, fullMark: 100 },
        { subject: 'Bonuses', A: 85, fullMark: 100 },
        { subject: 'Support', A: 90, fullMark: 100 },
        { subject: 'Security', A: 96, fullMark: 100 },
        { subject: 'Payout Speed', A: 88, fullMark: 100 },
        { subject: 'Mobile', A: 88, fullMark: 100 },
      ],
      monthlyData: [
        { month: 'Jan', withdrawals: 420, deposits: 1150, players: 3000 },
        { month: 'Feb', withdrawals: 490, deposits: 1350, players: 3300 },
        { month: 'Mar', withdrawals: 570, deposits: 1550, players: 3600 },
        { month: 'Apr', withdrawals: 640, deposits: 1750, players: 3900 },
        { month: 'May', withdrawals: 710, deposits: 1950, players: 4200 },
        { month: 'Jun', withdrawals: 780, deposits: 2150, players: 4500 },
      ],
      gameDistribution: [
        { name: 'Slots', value: 68, color: '#00f0ff' },
        { name: 'Table Games', value: 18, color: '#b026ff' },
        { name: 'Live Casino', value: 11, color: '#ff00f5' },
        { name: 'Jackpots', value: 3, color: '#00ff88' },
      ]
    }
  };

  const casino = casinoData[id || ''] || casinoData.allbritishcasino;

  return (
    <>
      {/* JSON-LD SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Review",
            "itemReviewed": {
              "@type": "Organization",
              "name": casino.name,
              "image": `https://spintitanium.com${casino.image}`
            },
            "reviewRating": {
              "@type": "Rating",
              "ratingValue": casino.rating,
              "bestRating": "5"
            },
            "author": {
              "@type": "Organization",
              "name": "SpinTitanium"
            },
            "reviewBody": casino.description
          })
        }}
      />

      <div className="min-h-screen bg-titanium-950 pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-titanium-900 to-titanium-950 py-12">
          <div className="container mx-auto px-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-neon-blue hover:text-neon-purple transition-colors mb-6"
            >
              <FaArrowLeft />
              <span>Back to Home</span>
            </Link>

            <div className="flex flex-col md:flex-row items-center gap-8">
              <img
                src={casino.image}
                alt={casino.name}
                className="h-32 w-auto"
                loading="eager"
              />
              <div className="flex-1">
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{casino.name}</h1>
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    {[...Array(5)].map((_, i) => (
                      <FaStar
                        key={i}
                        className={i < Math.floor(casino.rating) ? 'text-yellow-400 fill-current' : 'text-gray-600'}
                      />
                    ))}
                    <span className="text-2xl font-bold text-white ml-2">{casino.rating}</span>
                  </div>
                  <span className="text-gray-400">({casino.totalReviews} reviews)</span>
                </div>
                <p className="text-gray-300 text-lg mb-6">{casino.description}</p>
                {casino.url && (
                  <a
                    href={casino.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-neon-blue to-neon-purple hover:from-neon-purple hover:to-neon-pink text-white font-bold py-3 px-8 rounded-lg transition-all duration-300 transform hover:scale-105"
                  >
                    Play Now at {casino.name}
                    <FaArrowLeft className="rotate-180" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Tabs */}
        <section className="border-b border-titanium-800">
          <div className="container mx-auto px-4">
            <div className="flex gap-4 overflow-x-auto">
              {[
                { id: 'overview', label: 'Overview', icon: FaChartBar },
                { id: 'games', label: 'Games', icon: FaGamepad },
                { id: 'bonuses', label: 'Bonuses', icon: FaGift },
                { id: 'reviews', label: 'Reviews', icon: FaUsers },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-2 px-6 py-4 font-semibold transition-colors border-b-2 ${
                    activeTab === tab.id
                      ? 'text-neon-blue border-neon-blue'
                      : 'text-gray-400 border-transparent hover:text-gray-300'
                  }`}
                >
                  <tab.icon />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            {activeTab === 'overview' && (
              <div className="space-y-12">
                {/* Key Stats */}
                <div className="grid md:grid-cols-4 gap-6">
                  {[
                    { icon: FaShieldAlt, label: 'License', value: casino.license },
                    { icon: FaClock, label: 'Established', value: casino.established },
                    { icon: FaCreditCard, label: 'Min Deposit', value: casino.minDeposit },
                    { icon: FaClock, label: 'Withdrawal', value: casino.withdrawalTime },
                  ].map((stat, i) => (
                    <div key={i} className="glass rounded-xl p-6 border-2 border-titanium-800">
                      <stat.icon className="text-3xl text-neon-blue mb-3" />
                      <div className="text-gray-400 text-sm mb-1">{stat.label}</div>
                      <div className="text-white font-bold">{stat.value}</div>
                    </div>
                  ))}
                </div>

                {/* Radar Chart */}
                <div className="glass rounded-2xl p-8 border-2 border-titanium-800">
                  <h2 className="text-2xl font-bold text-white mb-6">Performance Overview</h2>
                  <ResponsiveContainer width="100%" height={400}>
                    <RadarChart data={casino.radarData}>
                      <PolarGrid stroke="#334e68" />
                      <PolarAngleAxis dataKey="subject" stroke="#9fb3c8" />
                      <PolarRadiusAxis angle={90} domain={[0, 100]} stroke="#9fb3c8" />
                      <Radar
                        name="Score"
                        dataKey="A"
                        stroke="#00f0ff"
                        fill="#00f0ff"
                        fillOpacity={0.6}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>

                {/* Monthly Data Chart */}
                <div className="glass rounded-2xl p-8 border-2 border-titanium-800">
                  <h2 className="text-2xl font-bold text-white mb-6">Monthly Activity</h2>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={casino.monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334e68" />
                      <XAxis dataKey="month" stroke="#9fb3c8" />
                      <YAxis stroke="#9fb3c8" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#102a43',
                          border: '1px solid #334e68',
                          borderRadius: '8px',
                          color: '#fff'
                        }}
                      />
                      <Bar dataKey="withdrawals" fill="#00f0ff" />
                      <Bar dataKey="deposits" fill="#b026ff" />
                      <Bar dataKey="players" fill="#ff00f5" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Pros & Cons */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="glass rounded-xl p-6 border-2 border-green-500/30">
                    <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                      <FaCheckCircle className="text-green-400" />
                      Pros
                    </h3>
                    <ul className="space-y-2">
                      {casino.pros.map((pro: string, i: number) => (
                        <li key={i} className="flex items-start gap-2 text-gray-300">
                          <FaCheckCircle className="text-green-400 flex-shrink-0 mt-1" />
                          <span>{pro}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="glass rounded-xl p-6 border-2 border-red-500/30">
                    <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                      <FaTimesCircle className="text-red-400" />
                      Cons
                    </h3>
                    <ul className="space-y-2">
                      {casino.cons.map((con: string, i: number) => (
                        <li key={i} className="flex items-start gap-2 text-gray-300">
                          <FaTimesCircle className="text-red-400 flex-shrink-0 mt-1" />
                          <span>{con}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'games' && (
              <div className="space-y-8">
                <div className="glass rounded-2xl p-8 border-2 border-titanium-800">
                  <h2 className="text-2xl font-bold text-white mb-6">Game Distribution</h2>
                  <div className="grid md:grid-cols-2 gap-8 items-center">
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={casino.gameDistribution}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(props: any) => {
                            const { name, percent } = props;
                            return `${name || ''} ${((percent || 0) * 100).toFixed(0)}%`;
                          }}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {casino.gameDistribution.map((entry: any, index: number) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-4">
                      {Object.entries(casino.games).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between p-4 bg-titanium-900 rounded-lg">
                          <span className="text-gray-300 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                          <span className="text-neon-blue font-bold text-xl">{String(value)}+</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'bonuses' && (
              <div className="space-y-6">
                <div className="glass rounded-2xl p-8 border-2 border-neon-blue/30">
                  <h2 className="text-2xl font-bold text-white mb-4">Welcome Bonus</h2>
                  <div className="bg-gradient-to-r from-neon-blue to-neon-purple text-white font-bold text-3xl py-4 px-6 rounded-lg mb-6 inline-block">
                    {casino.bonus}
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <div className="text-gray-400 text-sm mb-1">Wagering Requirement</div>
                      <div className="text-white font-bold">{casino.wagering}</div>
                    </div>
                    <div>
                      <div className="text-gray-400 text-sm mb-1">Max Bet</div>
                      <div className="text-white font-bold">{casino.maxBet}</div>
                    </div>
                    <div>
                      <div className="text-gray-400 text-sm mb-1">Valid For</div>
                      <div className="text-white font-bold">{casino.validFor}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="space-y-6">
                <div className="glass rounded-xl p-6 border-2 border-titanium-800">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-white">Player Reviews</h3>
                    <div className="text-2xl font-bold text-white">{casino.totalReviews} Reviews</div>
                  </div>
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="bg-titanium-900 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex text-yellow-400">
                            {[...Array(5)].map((_, j) => (
                              <FaStar key={j} className="fill-current" />
                            ))}
                          </div>
                          <span className="text-gray-400 text-sm">Verified Player</span>
                        </div>
                        <p className="text-gray-300">
                          Excellent casino with fast payouts and great customer service. The game selection is impressive and the bonuses are fair.
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 bg-gradient-to-br from-titanium-900 to-titanium-950">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to Play at {casino.name}?</h2>
            {casino.url && (
              <a
                href={casino.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-neon-blue to-neon-purple hover:from-neon-purple hover:to-neon-pink text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105"
              >
                <FaRocket />
                Claim Your Bonus Now
              </a>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default CasinoReview;

